import { useEffect, useMemo, useState } from "react";

import { type FindingSeverity } from "@/lib/api/building";
import {
  resolvedEquipmentZoneIds,
  type EquipmentCard,
  type Zone,
} from "@/lib/building-domain";
import {
  equipmentTypeIcon,
  equipmentTypeNameFi,
  useEquipmentTypeMeta,
  useKbSensorTypes,
} from "@/lib/equipment-type-meta";
import { inputGlowWrap, inputGlowInner } from "@/lib/card-styles";

import { EquipmentMonitorCard } from "./EquipmentCard";
import { EquipmentDetailPanel } from "./EquipmentDetailPanel";
import { EquipmentForm } from "./EquipmentForm";
import { EquipmentTile } from "./EquipmentTile";
import {
  next_open_equipment_id,
  reconcile_open_equipment_id,
} from "./equipment-row-chrome";

// ── Interfaces ────────────────────────────────────────────────────────────────

interface Props {
  buildingId: string;
  equipment: EquipmentCard[];
  zones: Zone[];
  locations: string[];
  operator: boolean;
  configurator: boolean;
  on_create: (data: {
    name: string;
    equipment_type: string;
    equipment_tag: string;
    zone_ids: string[];
    location: string | null;
  }) => Promise<void>;
  on_update: (
    id: string,
    data: {
      name?: string;
      equipment_type?: string;
      equipment_tag?: string;
      location?: string | null;
      state?: Record<string, unknown>;
      optimization_enabled?: boolean;
      declared_variants?: string[];
      declared_component_order?: string[];
      declared_component_subtypes?: Record<string, string>;
      declared_stream_component_order?: Record<string, string[]>;
    },
  ) => Promise<void>;
  on_delete: (id: string) => Promise<void>;
  on_add_zone_link: (equipmentId: string, zoneId: string) => Promise<void>;
  on_remove_zone_link: (equipmentId: string, zoneId: string) => Promise<void>;
  behavior_labels: Record<string, string>;
  findings_severity: Map<string, FindingSeverity>;
}

// ── EquipmentGrid ─────────────────────────────────────────────────────────────

export function EquipmentGrid({
  buildingId,
  equipment,
  zones,
  locations,
  behavior_labels,
  operator,
  configurator,
  on_create,
  on_update,
  on_delete,
  on_add_zone_link,
  on_remove_zone_link,
  findings_severity,
}: Props) {
  const eqMeta = useEquipmentTypeMeta();
  const sensors = useKbSensorTypes();
  const [open_id, set_open_id] = useState<string | null>(null);
  const [open_equipment_id, set_open_equipment_id] = useState<string | null>(
    null,
  );
  const [accordion_initialized, set_accordion_initialized] = useState(false);
  const [add_open, set_add_open] = useState(false);
  const [filter_zone, set_filter_zone] = useState("");
  const [filter_location, set_filter_location] = useState<string>("");
  const [filter_process, set_filter_process] = useState("");
  const [search, set_search] = useState("");

  const used_processes = useMemo(
    () => [...new Set(equipment.map((e) => e.equipment_type))],
    [equipment],
  );

  // Sijainti is single-sourced from the building's defined locations (getBuildingLocations),
  // not from free-text equipment.location values — so stale/legacy strings never surface here.
  const location_set = useMemo(() => new Set(locations), [locations]);

  const visible = useMemo(() => {
    return equipment.filter((e) => {
      const eq_zone_ids = resolvedEquipmentZoneIds(e);
      if (filter_zone === "__none__") {
        if (eq_zone_ids.length > 0) return false;
      } else if (filter_zone) {
        if (!eq_zone_ids.includes(filter_zone)) return false;
      }
      if (filter_location === "__none__") {
        if (e.location && location_set.has(e.location)) return false;
      } else if (filter_location !== "" && e.location !== filter_location) {
        return false;
      }
      if (filter_process && e.equipment_type !== filter_process) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          e.name.toLowerCase().includes(q) ||
          e.equipment_tag.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [
    equipment,
    filter_zone,
    filter_location,
    filter_process,
    search,
    location_set,
  ]);

  const floor_groups = useMemo(() => {
    const map = new Map<string, EquipmentCard[]>();
    for (const e of visible) {
      const key = e.location && location_set.has(e.location) ? e.location : "";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    const entries = [...map.entries()];
    entries.sort(([a], [b]) => {
      if (a === "") return 1;
      if (b === "") return -1;
      return a.localeCompare(b, "fi");
    });
    return entries;
  }, [visible, location_set]);

  const visible_ids = useMemo(
    () => floor_groups.flatMap(([, eqs]) => eqs.map((e) => e.id)),
    [floor_groups],
  );

  useEffect(() => {
    const next = reconcile_open_equipment_id(
      open_equipment_id,
      visible_ids,
      accordion_initialized,
    );
    if (next.open_id !== open_equipment_id) {
      set_open_equipment_id(next.open_id);
    }
    if (next.initialized !== accordion_initialized) {
      set_accordion_initialized(next.initialized);
    }
  }, [visible_ids, open_equipment_id, accordion_initialized]);

  return (
    <div className="flex flex-1 flex-col gap-3">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex min-w-40 flex-1 flex-col gap-0.5">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Haku
          </span>
          <div className={inputGlowWrap}>
            <div className={inputGlowInner}>
              <input
                value={search}
                onChange={(e) => set_search(e.target.value)}
                placeholder="Hae nimellä tai tunnuksella…"
                className="w-full bg-transparent px-3 py-1.5 text-sm placeholder:text-muted-foreground outline-none"
              />
            </div>
          </div>
        </div>
        {zones.length > 0 && (
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Palvelee vyöhykettä
            </span>
            <select
              value={filter_zone}
              onChange={(e) => set_filter_zone(e.target.value)}
              className="rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60"
            >
              <option value="">Kaikki</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
              <option value="__none__">Ei vyöhykettä</option>
            </select>
          </div>
        )}
        {locations.length > 0 && (
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Sijainti
            </span>
            <select
              value={filter_location}
              onChange={(e) => set_filter_location(e.target.value)}
              className="rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60"
            >
              <option value="">Kaikki</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
              <option value="__none__">Ei sijaintia</option>
            </select>
          </div>
        )}
        {used_processes.length > 1 && (
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Tyyppi
            </span>
            <select
              value={filter_process}
              onChange={(e) => set_filter_process(e.target.value)}
              className="rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60"
            >
              <option value="">Kaikki</option>
              {used_processes.map((t) => (
                <option key={t} value={t}>
                  {equipmentTypeIcon(eqMeta, t, sensors)}{" "}
                  {equipmentTypeNameFi(eqMeta, t, sensors)}
                </option>
              ))}
            </select>
          </div>
        )}
        {operator && (
          <button
            onClick={() => set_add_open((v) => !v)}
            className="ml-auto self-end rounded-lg border border-dashed border-border/40 px-3 py-1.5 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
          >
            + Lisää talotekniikka
          </button>
        )}
      </div>

      {add_open && (
        <EquipmentForm
          zones={zones}
          locations={locations}
          on_submit={async (data) => {
            await on_create(data);
            set_add_open(false);
          }}
          on_cancel={() => set_add_open(false)}
        />
      )}

      {visible.length === 0 && !add_open ? (
        <p className="mt-8 text-center text-sm text-muted-foreground">
          {equipment.length === 0
            ? "Ei taloteknisiä laitteita. Lisää laite yllä olevalla painikkeella."
            : "Ei tuloksia haulla."}
        </p>
      ) : (
        <div className="flex flex-col gap-5">
          {floor_groups.map(([location_key, floor_eqs]) => (
            <div key={location_key || "__none__"}>
              {floor_groups.length > 1 && (
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                  {location_key || "Ei sijaintia"}
                </p>
              )}
              {operator ? (
                /* Config mode — clicking a card opens its structure edit form (name/type/
                   location/zones/variants). Behaviours & signals live in the Kyvykkyydet /
                   Signaalit tabs, not here. */
                <div className="flex flex-col gap-2">
                  {floor_eqs.map((e) => (
                    <EquipmentTile
                      key={e.id}
                      buildingId={buildingId}
                      equipment={e}
                      zones={zones}
                      locations={locations}
                      operator={operator}
                      configurator={configurator}
                      max_severity={findings_severity.get(e.id)}
                      open={open_equipment_id === e.id}
                      on_toggle={() =>
                        set_open_equipment_id(
                          next_open_equipment_id(open_equipment_id, e.id),
                        )
                      }
                      on_update={async (id, data) => {
                        await on_update(id, data);
                      }}
                      on_delete={async (id) => {
                        await on_delete(id);
                      }}
                      on_add_zone_link={on_add_zone_link}
                      on_remove_zone_link={on_remove_zone_link}
                    />
                  ))}
                </div>
              ) : (
                /* Monitoring mode — card grid, live readings on each card */
                <div className="grid grid-cols-2 items-stretch gap-3 lg:grid-cols-3">
                  {floor_eqs.map((e) => (
                    <EquipmentMonitorCard
                      key={e.id}
                      equipment={e}
                      zones={zones}
                      behavior_labels={behavior_labels}
                      max_severity={findings_severity.get(e.id)}
                      expanded={open_id === e.id}
                      on_toggle={() =>
                        set_open_id(open_id === e.id ? null : e.id)
                      }
                    />
                  ))}
                  {open_id !== null &&
                    floor_eqs.some((e) => e.id === open_id) &&
                    (() => {
                      const active = floor_eqs.find((e) => e.id === open_id);
                      if (!active) return null;
                      return (
                        <EquipmentDetailPanel
                          key={active.id}
                          buildingId={buildingId}
                          equipment={active}
                          zones={zones}
                          behavior_labels={behavior_labels}
                          operator={operator}
                          on_update={on_update}
                          on_close={() => set_open_id(null)}
                        />
                      );
                    })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
